import { cx } from "@emotion/css";
import { space } from "@octopusdeploy/design-system-tokens";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { ChannelResource, EnvironmentV2Resource, FeedResource, GetTeamsLookupBffResponseTeam, GitRef, GitRefResource, PackageReference, PlatformHubAccountSummary, ProjectSummaryResource, PropertyValueResource, ScopeValues, TemplateParameterResource, VariableSetResource, WorkerPoolResource, } from "@octopusdeploy/octopus-server-client";
import { ControlType, getParametersOfType, isAccountControlType } from "@octopusdeploy/octopus-server-client";
import { noOp } from "@octopusdeploy/utilities";
import { useMemo, useState } from "react";
import type { StepEditorEvent } from "~/analytics/Analytics";
import { useScopeWidth } from "~/areas/platformHub/processEditor/useScopeWidth";
import type { ParameterValueRow, TemplateValueKey } from "~/areas/platformHub/processTemplates/TemplateParametersTable";
import { TemplateParameterScopeCell } from "~/areas/platformHub/processTemplates/TemplateParametersTable";
import type { FailedPermissionCheck } from "~/areas/projects/components/Process/ProcessStepsLayoutTypes";
import { isFailedPermissionCheck } from "~/areas/projects/components/Process/ProcessStepsLayoutTypes";
import { ClearOldParameterValuesCallout } from "~/areas/projects/components/Process/ProcessTemplates/ConsumerProcessTemplateParametersTab";
import { defaultScopeValues, getAllVariables, getControlTypeFromParameterValueRow, getUniqueAccountVariableNames, shouldRenderParameter, templateParameterValueStyles, } from "~/areas/projects/components/Process/ProcessTemplates/TemplateParameterHelpers";
import { showMultipleValuesAndScopesIndicators, TemplateParameterValueCell } from "~/areas/projects/components/Process/ProcessTemplates/TemplateParameterValueCell";
import { toTemplatedProjectProcessIdentifier } from "~/areas/projects/components/Process/types";
import type { LoadedLibraryVariableSets } from "~/areas/projects/components/Variables/AllVariables/SharedAllVariablesPage";
import { getVariableTypeFromDisplaySettings } from "~/areas/projects/components/Variables/TenantVariables/getVariableTypeFromDisplaySettings";
import { useProjectContext } from "~/areas/projects/context";
import { isAccountType } from "~/areas/variables/isAccountType";
import { repository } from "~/clientInstance";
import type { ProjectSourceItems } from "~/components/ActionTemplateParameterInput/ActionTemplateParameterInput";
import ActionTemplateParameterInput from "~/components/ActionTemplateParameterInput/ActionTemplateParameterInput";
import { type DoBusyTask } from "~/components/DataBaseComponent/index";
import { GroupedDataTable } from "~/components/GroupedDataTable/GroupedDataTable";
import Markdown from "~/components/Markdown/index";
import { getAllProjectActionTemplates } from "~/utils/LoadProjectVariables/loadProjectVariables";
interface TemplatedProjectParameterValuesProps {
    doBusyTask: DoBusyTask;
    onParameterValueChanged: (parameterName: string, value: PropertyValueResource | undefined) => void;
    parameters: TemplateParameterResource[];
    parameterValues: Record<string, PropertyValueResource | undefined>;
    gitRefResource: GitRefResource | undefined;
    projectVariableSet: VariableSetResource | FailedPermissionCheck;
    libraryVariableSets: LoadedLibraryVariableSets[] | FailedPermissionCheck;
    environments: EnvironmentV2Resource[];
    analyticsStepEditorDispatch: (name: string, event: StepEditorEvent) => void;
    availableWorkerPools: WorkerPoolResource[];
    machineRoles: string[];
    teams: GetTeamsLookupBffResponseTeam[];
    isTenanted: boolean;
    channels: ChannelResource[];
    projects: ProjectSummaryResource[];
    feeds: FeedResource[];
    refreshVariables: () => Promise<void>;
    refreshFeeds: () => Promise<void>;
    platformHubDefaultGitRef: GitRef;
}
export function TemplatedProjectParameterValues({ doBusyTask, onParameterValueChanged, parameters, parameterValues, gitRefResource, projectVariableSet, libraryVariableSets, environments, analyticsStepEditorDispatch, availableWorkerPools, machineRoles, isTenanted, teams, channels, projects, feeds, refreshVariables, refreshFeeds, }: TemplatedProjectParameterValuesProps) {
    const projectContext = useProjectContext();
    const [parameterWorkerPools, setParameterWorkerPools] = useState(availableWorkerPools);
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const platformHubAccountIds = useMemo(() => parameters.filter((p) => isAccountType(getVariableTypeFromDisplaySettings(p.DisplaySettings["Octopus.ControlType"]))).flatMap((p) => p.Values.map((v) => v.Value as string)), [parameters]);
    const { result: platformHubAccountNames } = useQuery(() => repository.PlatformHubAccounts.getAccountNames(platformHubAccountIds), [platformHubAccountIds], "Get platform hub account names");
    const project = projectContext.state.model;
    /* eslint-disable @typescript-eslint/no-non-null-assertion */
    const templatedProjectIdentifier = useMemo(() => toTemplatedProjectProcessIdentifier(project, project.ProjectTemplateDetails!), [project]);
    const packageReferences: PackageReference[] = getParametersOfType(parameters, ControlType.Package).map((p) => ({
        Name: p.Name,
        PackageId: "",
        FeedId: "",
        AcquisitionLocation: "",
        Properties: {
            PackageParameterName: p.Name,
            SelectionMode: "deferred",
        },
        Id: "",
    }));
    if (!platformHubAccountNames) {
        return null;
    }
    const sourceItems: ProjectSourceItems = {
        projectId: templatedProjectIdentifier.projectId,
        stepNames: [],
        variableSet: projectVariableSet,
        libraryVariableSets,
        processIdentifier: templatedProjectIdentifier,
        refreshVariables,
        gitRef: gitRefResource,
        packages: {
            items: packageReferences,
            feeds,
            onRequestRefresh: async () => {
                await doBusyTask(async () => {
                    await refreshFeeds();
                });
            },
            // Process templates use this to set the action package property metadata.
            // This may not be relevant for project templates, or is at least something we'll have to handle as a special case.
            setPackages: () => noOp(),
        },
        actionTemplateParameters: getAllProjectActionTemplates(projectContext.state.model, isFailedPermissionCheck(libraryVariableSets) ? [] : libraryVariableSets.map((lvs) => lvs.libraryVariableSet)),
        availableTargetTags: machineRoles,
        environments,
        workerPools: {
            items: parameterWorkerPools,
            onRequestRefresh: () => doBusyTask(async () => {
                const summaries = await repository.WorkerPools.summary();
                setParameterWorkerPools(summaries.WorkerPoolSummaries.map((s) => s.WorkerPool));
            }),
        },
        teams,
        channels,
        projects,
    };
    const parametersToRender = parameters.filter((p) => shouldRenderParameter(p, parameterValues[p.Name], availableWorkerPools, isTenanted));
    const parameterValueRows = getParameterValueRows(parametersToRender, parameterValues, sourceItems);
    const sortedParameterValueRows = [...parameterValueRows.filter((row) => isAccountControlType(getControlTypeFromParameterValueRow(row))), ...parameterValueRows.filter((row) => !isAccountControlType(getControlTypeFromParameterValueRow(row)))];
    const parameterNamesInParameters = new Set(parameters.map((p) => p.Name));
    const parametersRemovedFromProjectTemplate = Object.keys(parameterValues).filter((key) => !parameterNamesInParameters.has(key));
    const clearValues = () => {
        parametersRemovedFromProjectTemplate.forEach((key) => {
            onParameterValueChanged(key, undefined);
        });
    };
    return (<>
            {parametersToRender.length > 0 ? (<>
                    {parametersRemovedFromProjectTemplate.length > 0 ? <ClearOldParameterValuesCallout onClearValues={clearValues}/> : null}
                    <TemplatedProjectParameterValuesTable parameterValueRows={sortedParameterValueRows} availableScopeValues={{ ...defaultScopeValues, Environments: environments }} platformHubAccounts={platformHubAccountNames} sourceItems={sourceItems} parameterValues={parameterValues} onParameterValueChanged={onParameterValueChanged} doBusyTask={doBusyTask} analyticsStepEditorDispatch={analyticsStepEditorDispatch}/>
                </>) : null}
        </>);
}
interface TemplatedProjectParameterValuesTableProps {
    parameterValueRows: ParameterValueRow[];
    availableScopeValues: ScopeValues;
    platformHubAccounts: PlatformHubAccountSummary[];
    sourceItems: ProjectSourceItems;
    parameterValues: Record<string, PropertyValueResource | undefined>;
    onParameterValueChanged: (parameterName: string, value: PropertyValueResource | undefined) => void;
    doBusyTask: DoBusyTask;
    analyticsStepEditorDispatch: (name: string, event: StepEditorEvent) => void;
    hideScopeColumn?: boolean;
}
function TemplatedProjectParameterValuesTable({ parameterValueRows, availableScopeValues, platformHubAccounts, sourceItems, parameterValues, onParameterValueChanged, doBusyTask, analyticsStepEditorDispatch, hideScopeColumn, }: TemplatedProjectParameterValuesTableProps) {
    const { scopeHeaderRef, scopeWidth } = useScopeWidth();
    const [focusedValueKey, setFocusedValueKey] = useState<TemplateValueKey | null>(null);
    const nameColumnWidthAsPercentage = 26;
    const valueAndScopeColumnWidthAsPercentage = 37;
    return (<div>
            <GroupedDataTable data={parameterValueRows} columns={[
            {
                title: "Name",
                render: (data) => data.ValueIndex === 0 ? (<div>
                                    <div className={styles.parameterNameCellPrimaryHeader}>{data.Parameter.Label || data.Parameter.Name}</div>
                                    {data.Parameter.Label && <div className={styles.parameterNameCellSecondaryHeader}>{data.Parameter.Name}</div>}
                                    <div className={styles.parameterHelpText}>
                                        <Markdown markup={data.Parameter.HelpText} noMargins={true} mutedTextColor={true}/>
                                    </div>
                                </div>) : null,
                width: `${nameColumnWidthAsPercentage}%`,
                isChild: (data) => data.ValueIndex !== 0,
                padding: space[12],
                collapseNullCellsUpwards: true,
                verticalAlign: "top",
            },
            {
                title: "Value",
                render: (data) => (<TemplateParameterValueCell data={data} sourceItems={sourceItems} parameterValues={parameterValues} onParameterValueChanged={onParameterValueChanged} doBusyTask={doBusyTask} analyticsStepEditorDispatch={analyticsStepEditorDispatch} platformHubAccounts={platformHubAccounts} focusedValueKey={focusedValueKey} setFocusedValueKey={setFocusedValueKey} renderParameterEditor={({ parameter, autoFocus }) => (<TemplatedProjectParameter key={parameter.Name} parameter={parameter} sourceItems={sourceItems} parameterValues={parameterValues} onParameterValueChanged={onParameterValueChanged} doBusyTask={doBusyTask} analyticsStepEditorDispatch={analyticsStepEditorDispatch} autoFocus={autoFocus}/>)}/>),
                width: hideScopeColumn ? `${2 * valueAndScopeColumnWidthAsPercentage}%` : `${valueAndScopeColumnWidthAsPercentage}%`,
                padding: "0",
            },
            ...(hideScopeColumn
                ? []
                : [
                    {
                        title: "Scope",
                        render: (data: ParameterValueRow) => {
                            const controlType = data.Parameter.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText;
                            const getScopeCellContents = () => {
                                if (!data.Value || !data.Value.Scope) {
                                    return null;
                                }
                                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                                if (getUniqueAccountVariableNames(sourceItems).includes(data.Value.Value as string)) {
                                    return <div className={cx(styles.disabledText, styles.valueContainer)}>Defined by variable</div>;
                                }
                                return (<TemplateParameterScopeCell parameter={data.Parameter} value={data.Value} onChange={noOp} index={data.ValueIndex} isEditing={false} focusedValueKey={focusedValueKey} setFocusedValueKey={setFocusedValueKey} scopeValues={availableScopeValues || { Actions: [], Channels: [], Environments: [], Machines: [], Roles: [], TenantTags: [], Processes: [] }} height={48} width={scopeWidth} isConsumer={true}/>);
                            };
                            return (<div>
                                              {showMultipleValuesAndScopesIndicators(controlType, data) && (<div className={styles.multipleValuesAndScopesIndicator}>
                                                      <div>Multiple scopes</div>
                                                  </div>)}
                                              <div className={styles.scopeContainer}>{getScopeCellContents()}</div>
                                          </div>);
                        },
                        width: `${valueAndScopeColumnWidthAsPercentage}%`,
                        padding: "0",
                        ref: scopeHeaderRef,
                    },
                ]),
        ]} getRowKey={(data) => `${data.Parameter.Name}-${data.ValueIndex}`}/>
        </div>);
}
interface TemplatedProjectParameterProps {
    parameter: TemplateParameterResource;
    sourceItems: ProjectSourceItems;
    parameterValues: Record<string, PropertyValueResource | undefined>;
    onParameterValueChanged: (parameterName: string, value: PropertyValueResource | undefined) => void;
    doBusyTask: DoBusyTask;
    analyticsStepEditorDispatch: (name: string, event: StepEditorEvent) => void;
    autoFocus: boolean;
}
function TemplatedProjectParameter({ parameter, sourceItems, parameterValues, onParameterValueChanged, doBusyTask, analyticsStepEditorDispatch, autoFocus }: TemplatedProjectParameterProps) {
    const type = parameter.DisplaySettings["Octopus.ControlType"];
    const value = parameterValues[parameter.Name];
    // We do not have a process to use for error handling here. This may need some more consideration in the future.
    //const processErrorSelectors = useProcessErrorSelectors();
    //const getStepFieldError = (value: string) => processErrorSelectors.getStepFieldError(stepId, value);
    return type ? (<div className={styles.parameterContainer}>
            <ActionTemplateParameterInput parameter={parameter} sourceItems={sourceItems} doBusyTask={doBusyTask} value={value} onChange={(value: PropertyValueResource | undefined) => onParameterValueChanged(parameter.Name, value)} actionType="Octopus.ProjectTemplate" 
    //error={getStepFieldError(parameter.Name)}
    analyticsStepEditorDispatch={analyticsStepEditorDispatch} autoFocus={autoFocus}/>
        </div>) : null;
}
const styles = templateParameterValueStyles;
function getParameterValueRows(parameters: TemplateParameterResource[], parameterValues: Record<string, PropertyValueResource | undefined>, sourceItems: ProjectSourceItems): ParameterValueRow[] {
    return parameters.flatMap((p, parameterIndex): ParameterValueRow[] => {
        let parameterWithOverrides = { ...p };
        let overrideVariableRow: ParameterValueRow | undefined = undefined;
        let startingValueIndex = 0;
        const controlType = p.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText;
        const overrideVariableName = parameterValues[p.Name];
        if (!!parameterValues[p.Name] && typeof overrideVariableName === "string" && isAccountControlType(controlType)) {
            overrideVariableRow = {
                Parameter: p,
                Value: { Value: overrideVariableName, Scope: {}, OverriddenScope: {} },
                ParameterIndex: parameterIndex,
                ValueIndex: startingValueIndex,
            };
            const completeOverrideVariableEnvironmentScope = getAllVariables(sourceItems)
                .filter((variable) => variable.Name === overrideVariableName)
                .flatMap((variable) => variable.Scope?.Environment?.filter((e) => e !== undefined) ?? []);
            parameterWithOverrides = {
                ...p,
                Values: p.Values.map((v) => ({
                    ...v,
                    OverriddenScope: { Environment: completeOverrideVariableEnvironmentScope },
                    OverrideValue: overrideVariableName,
                })),
            };
            startingValueIndex = 1;
        }
        else if (p.Values.length === 0) {
            return [{ Parameter: p, Value: null, ParameterIndex: parameterIndex, ValueIndex: 0 }];
        }
        return [
            ...(overrideVariableRow ? [overrideVariableRow] : []),
            ...parameterWithOverrides.Values.map<ParameterValueRow>((v, valueIndex) => ({ Parameter: parameterWithOverrides, Value: v, ParameterIndex: parameterIndex, ValueIndex: startingValueIndex + valueIndex })),
        ];
    });
}
